import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Info, Target, User } from 'lucide-react';
import InstitutionalVideoModal from './InstitutionalVideoModal';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * AboutMenu — Phase B · Workstream 4
 * Compact "About SHS" dropdown that surfaces the three institutional videos
 * from the Founder/SHS profile presentation. Fully keyboard-accessible,
 * respects prefers-reduced-motion (no autoplay).
 */
export default function AboutMenu({ triggerClassName = '' }) {
  const [open, setOpen] = useState(false);
  const [activeVideo, setActiveVideo] = useState(null);
  const menuRef = useRef(null);
  const btnRef = useRef(null);
  const { language } = useLanguage();

  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target) && !btnRef.current?.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  const items = [
    { id: 'about-shs',      icon: Info,   label_en: 'What is SHS?',     label_es: '¿Qué es SHS?' },
    { id: 'mission-vision', icon: Target, label_en: 'Mission & Vision', label_es: 'Misión y visión' },
    { id: 'founder-intro',  icon: User,   label_en: 'Meet the Founder', label_es: 'Conoce al fundador' },
  ];

  return (
    <>
      <div className="relative inline-block">
        <button
          ref={btnRef}
          onClick={() => setOpen((v) => !v)}
          className={`inline-flex items-center gap-1 text-sm xl:text-[13px] whitespace-nowrap ${triggerClassName}`}
          aria-haspopup="menu"
          aria-expanded={open}
          data-testid="about-menu-trigger"
        >
          <span>{language === 'es' ? 'Conozca SHS' : 'Meet SHS'}</span>
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
        </button>
        {open && (
          <div
            ref={menuRef}
            role="menu"
            className="absolute right-0 mt-2 w-64 bg-slate-900 border border-slate-800 rounded-lg shadow-xl overflow-hidden z-40"
            data-testid="about-menu-panel"
          >
            <ul className="py-1">
              {items.map((it) => {
                const Icon = it.icon;
                const label = language === 'es' ? it.label_es : it.label_en;
                return (
                  <li key={it.id}>
                    <button
                      role="menuitem"
                      onClick={() => { setOpen(false); setActiveVideo(it.id); }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-slate-100 hover:bg-slate-800 focus:bg-slate-800 focus:outline-none min-h-[44px]"
                      data-testid={`about-menu-item-${it.id}`}
                    >
                      <Icon className="w-4 h-4 text-cyan-400 shrink-0" aria-hidden="true" />
                      <span className="flex-1">{label}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>

      <InstitutionalVideoModal
        videoId={activeVideo}
        isOpen={Boolean(activeVideo)}
        onClose={() => setActiveVideo(null)}
        language={language}
      />
    </>
  );
}
